import { ReactNode } from 'react';
import { withStyles } from 'vopay';
import { motion } from 'motion/react';
import styles from './PermissionGroup.module.scss';

// A card of permission rows — moved from client-dashboard's SubAccounts `SettingsGroup` (ID-13343). This owns
// only the card's own show/hide (`animateShowHide`); the rows inside are wrapped in an `AnimatePresence` by
// the caller, next to where they're mapped (see PermissionContainer), so each animation lives with its list.

const cn = withStyles(styles);

// The gap between cards in a section, animated with the card's height so a whole card growing in or out
// carries its spacing with it (the parent `.settings` drops its flex `gap` for this — see PermissionContainer
// .module.scss). Only `animateShowHide` cards use it, so `.settings` needs every card to set the prop.
const CARD_GAP = 16;

type PermissionGroupProps = {
  children: ReactNode;
  className?: string;
  /** Animate the whole card's own show/hide — its height grows in and out as a dependency reveals or hides
      it, driven by the parent `.settings` AnimatePresence. Off (default) for cards that are always present,
      so they don't grow-in on first paint. (Rows inside are animated by the caller, not this prop.) */
  animateShowHide?: boolean;
};

export default function PermissionGroup({ children, className, animateShowHide = false }: PermissionGroupProps) {
  if (!animateShowHide) {
    return <div className={cn('settingsGroup', className)}>{children}</div>;
  }

  return (
    <motion.div
      className={cn('settingsGroup', className)}
      initial={{ opacity: 0, height: 0, marginBottom: 0 }}
      animate={{ opacity: 1, height: 'auto', marginBottom: CARD_GAP, transition: { duration: 0.2, ease: 'easeOut' } }}
      exit={{ opacity: 0, height: 0, marginBottom: 0, transition: { duration: 0.15, ease: 'easeIn' } }}
    >
      {children}
    </motion.div>
  );
}

type HeaderProps = {
  children: ReactNode;
  className?: string;
};

function Header({ children, className }: HeaderProps) {
  return <div className={cn('settingsHeader', className)}>{children}</div>;
}

PermissionGroup.Header = Header;

type RowProps = {
  children: ReactNode;
  className?: string;
};

function Row({ children, className }: RowProps) {
  return (
    <motion.div
      className={cn('settingsRow')}
      initial={{ opacity: 0, height: 0 }}
      animate={{
        opacity: 1,
        height: 'auto',
        transition: {
          duration: 0.2,
          ease: 'easeOut',
        },
      }}
      exit={{
        opacity: 0,
        height: 0,
        transition: {
          duration: 0.15,
          ease: 'easeIn',
        },
      }}
    >
      {/* The padding lives on an inner element so the row can collapse to a true zero height. */}
      <div className={cn('settingsRowContent', className)}>{children}</div>
    </motion.div>
  );
}

PermissionGroup.Row = Row;
